import { SectionType } from '@/types/store';
import { PageSchema } from '@/types/pageSchema';
import z from 'zod';
import { ItemType } from '../types/store';

// Data schemas
export const EditorialTextDataSchema = z.object({
    editorial_text: z.object({
        primary_text: z.string().min(1),
        secondary_text: z.string().optional(),
    }),
});

export const GalleryDataSchema = z.object({
    gallery: z.object({
        images: z.array(z.string()).min(1),
    }),
});

export const TestimonialsDataSchema = z.object({
    title: z.string().optional(),
    testimonials: z.object({
        testimonials: z
            .array(
                z.object({
                    image: z.string().url(),
                    quote: z.string().min(1),
                    source: z.string().optional(),
                })
            )
            .min(1),
    }),
});

export const TitleDataSchema = z.object({
    title: z.string().min(1),
});

export const EditorialImageDataSchema = z.object({
    image: z.string().url(),
});

export const HeaderDataSchema = z.object({
    title: z.string().min(1),
    bio: z.string().optional(),
    image: z.string().url(),
});

export const PendingDataSchema = z.object({}).optional();

// Base item fields (shared across all item types)
const baseItemFields = {
    id: z.union([z.number(), z.string()]),
    status: z.number(),
    store_id: z.number(),
    order: z.number(),
};

// Individual item schemas
export const EditorialTextItemSchema = z.object({
    ...baseItemFields,
    type: z.literal(SectionType.EDITORIAL_TEXT),
    data: EditorialTextDataSchema,
});

export const GalleryItemSchema = z.object({
    ...baseItemFields,
    type: z.literal(SectionType.GALLERY),
    data: GalleryDataSchema,
});

export const TestimonialsItemSchema = z.object({
    ...baseItemFields,
    type: z.literal(SectionType.TESTIMONIALS),
    data: TestimonialsDataSchema,
});

export const TitleItemSchema = z.object({
    ...baseItemFields,
    type: z.literal(SectionType.TITLE),
    data: TitleDataSchema,
});

export const PageItemSchema = z.object({
    ...baseItemFields,
    type: z.literal(ItemType.PAGE),
    data: PageSchema,
    user: z.any(),
});

export const PendingItemSchema = z.object({
    ...baseItemFields,
    type: z.literal(ItemType.PENDING),
    data: PendingDataSchema,
});

export const HeaderItemSchema = z.object({
    ...baseItemFields,
    type: z.literal(ItemType.HEADER),
    data: HeaderDataSchema,
});

// Discriminated union - Main StoreItem schema
export const StoreItemSchema = z.discriminatedUnion('type', [
    EditorialTextItemSchema,
    GalleryItemSchema,
    TestimonialsItemSchema,
    TitleItemSchema,
    PageItemSchema,
    PendingItemSchema,
    HeaderItemSchema,
]);

// Section items only (for validation that excludes PENDING)
export const SectionItemSchema = z.discriminatedUnion('type', [
    EditorialTextItemSchema,
    GalleryItemSchema,
    TestimonialsItemSchema,
    TitleItemSchema,
]);

// Validation schema (excludes PENDING type for publishing)
export const ValidItemSchema = z.discriminatedUnion('type', [
    EditorialTextItemSchema,
    GalleryItemSchema,
    TestimonialsItemSchema,
    TitleItemSchema,
    PageItemSchema,
    HeaderItemSchema,
]);

// Legacy support - ItemSchema alias for backwards compatibility
export const ItemSchema = StoreItemSchema;
